import { Modal } from 'react-bootstrap';
import { StyledEditApartmentPaymentsModal } from './EditApartmentPaymentsModal.styled';
import {useTranslation} from 'react-i18next';
import React, {Fragment, useEffect, useMemo, useState} from "react";
import {useAccessToken} from "../../../common/hooks/useAccessToken";
import { NotificationManager } from 'react-notifications';
import {CurrencyValue} from "../../../common/currencyValue";
import BllinkUtils from "../../../common/utils/utils";
import ContentLoader from "../../../common/simple/ContentLoader";
import Checkbox from "../../../common/simple/Checkbox";
import { DayPicker } from '../../../common/DayPicker/DayPicker';
import LeftArrow from "../../../images/newDesign/arrow_black.svg";
import {constants} from "../../../common/constants/constants";
import {useSelector} from "react-redux";
import {selectCurrentBuildingCurrency} from "../../../store/buildings/selectors";

export function EditApartmentPaymentsModal({ record, buildingID, year, show, onHide, paymentPlanId, setUpdatePayment}) {

    if (!show) {
        return '';
    }

    const { t } = useTranslation();
    const client = useAccessToken();

    const [loading, setLoading] = useState(false);
    const [apartmentNumber, setApartmentNumber] = useState(null);
    const [rows, setRows] = useState([]);
    const [rowsToCancel, setRowsToCancel] = useState({});
    const [updatedDateRows, setUpdatedDateRows] = useState({});
    const [selectedRows, setSelectedRows] = useState({});
    const [selectedDay, setSelectedDay] = useState(null);
    const [changeDateOpened, setChangeDateOpened] = useState(false);

    const currency = useSelector(selectCurrentBuildingCurrency);

    const selectedAmount = Object.keys(selectedRows).length;
    const chargeDates = Object.values(selectedRows).map(row => row.chargeDate);

    useMemo(() => setApartmentNumber((record?.apartmentNumber || record?.apartmentNum) ?? null), [record]);

    useEffect(async () => {
        if (!loading && apartmentNumber != null && buildingID != null) {
            setLoading(true);
            try {
                const loadedRows = await loadRows();
                setRows(loadedRows);
                setSelectedRows(loadedRows.reduce((acc, row) => Object.assign(acc, { [row.PaymentPlanDetailId]: row }), {}));
            } catch (e) {
                NotificationManager.error(e.message);
            } finally {
                setLoading(false);
            }
        }
    }, [apartmentNumber, buildingID]);


    async function loadRows() {
        // use year=null to show all active payyment plans for all years,
        // as users expect this behavior even in context of particular year of year report
        const pps = await client.managers.payments.getActivePaymentPlans(buildingID, apartmentNumber, null, paymentPlanId);
        const rr = pps.map(pp => {
            const rows = [];
            const PaymentPlanId = pp.PaymentPlanId;
            if (pp.OneTimePayment != null) {
                const paidFor = pp.OneTimePayment.PaymentSettings?.Description?.description;
                rows.push(...pp.PaymentPlanDetails.map(ppd => ({
                    PaymentPlanId,
                    PaymentPlanDetailId: ppd.PaymentPlanDetailId,
                    paidFor,
                    cartID: pp.cartID,
                    chargeDate: ppd.ChargeDate,
                    amountInCents: ppd.AmountInCents,
                    currency: currency
                })));
            } else {
                rows.push(...pp.PaymentPlanDetails.map(ppd => ({
                    PaymentPlanId,
                    PaymentPlanDetailId: ppd.PaymentPlanDetailId,
                    PaymentId: ppd.Payment?.PaymentId,
                    cartID: pp.cartID,
                    paidFor: ppd.Months.map(m =>`${t(`common.months.${m.month}`)} ${m.year}` ).join(', '),
                    chargeDate: ppd.ChargeDate,
                    amountInCents: ppd.AmountInCents,
                    currency: currency
                })));
            }
            return rows;
        });
        const allRows = rr.flat();
        return allRows;
    }

    async function handleApproveClick() {
        const cancelPaymentPlanDetailsIds = Object.keys(rowsToCancel).map(k => Number(k));
        const nothingToCancel = cancelPaymentPlanDetailsIds.length === 0;
        const noDatesToUpdate = Object.keys(updatedDateRows).length === 0;

        if (nothingToCancel && noDatesToUpdate) {
            NotificationManager.warning(t('reports.edit_apartment_payments.empty_selection'));
            return;
        }

        if (!nothingToCancel) {
            try {
                setLoading(true);
                const options = {};
                if (!noDatesToUpdate) {
                    options.withoutInvalidate = true;
                }
                await client.managers.payments.stopPaymentPlanDetails(buildingID, cancelPaymentPlanDetailsIds, options);
                NotificationManager.success(t('reports.edit_apartment_payments.stop_success'));
            } catch (e) {
                NotificationManager.error(e.message);
            }
        }

        if (!noDatesToUpdate) {
            try {
                setLoading(true);
                await client.managers.payments.updateChargeDatesPaymentPlanDetails(buildingID, updatedDateRows);
                NotificationManager.success(t('reports.edit_apartment_payments.charge_dates_success'));
            } catch (e) {
                NotificationManager.error(e.message);
            }
        }

        onHide(true, record);
        setLoading(false);
        setUpdatePayment(true);
    }

    function handleHideClick() {
        setRows([]);
        setRowsToCancel({});
        setUpdatedDateRows({});
        setApartmentNumber(null);
        setLoading(false);
        onHide(false, record);
    }

    function handleCheckboxChange(selected, row) {
        if (selected) {
            selectedRows[row.PaymentPlanDetailId] = row;
        } else {
            delete selectedRows[row.PaymentPlanDetailId];
        }
        setSelectedRows({...selectedRows});
    }

    function unselectAll() {
        setSelectedRows({});
    }

    function cancelPayments() {
        const obj = {};
        Object.keys(selectedRows).forEach(k => obj[+k] = true);
        setRowsToCancel(prev => ({
            ...prev,
            ...obj,
        }));
        unselectAll();
    }

    function openChangeDate() {
        setChangeDateOpened(true);
    }

    function closeChangeDate() {
        setChangeDateOpened(false);
    }

    function changeDay(day) {
        const result = {};
        Object.values(selectedRows).forEach(item => {
            const date = new Date(item.chargeDate);
            date.setUTCDate(day);
            result[item.PaymentPlanDetailId] = date.toISOString();
        });

        setSelectedDay(day);
        setUpdatedDateRows(prev => ({
            ...prev,
            ...result
        }));

        closeChangeDate();
        unselectAll();
    }

    return !show ? '' : (
        <StyledEditApartmentPaymentsModal show={show} onHide={handleHideClick} centered size='lg'>
            <span className="modal-close" onClick={handleHideClick}>&#215;</span>
            <Modal.Header>
                <h2>{t('reports.edit_apartment_payments.title', {apartmentNumber})}</h2>
                <p>{t('reports.edit_apartment_payments.subtitle')}</p>
            </Modal.Header>
            <Modal.Body>
                {loading ? <ContentLoader width="100%" height={200} /> : (rows.length === 0 ? <Fragment>
                    <div className="no-records">{t('reports.edit_apartment_payments.no_payments')}</div>
                </Fragment> : <Fragment>
                    <table>
                        <thead>
                        <tr>
                            <th></th>
                            <th>{t('reports.charge_date_title')}</th>
                            <th className="amount">{t('reports.amount')}</th>
                            <th>{t('reports.paid_for')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((row, i) => (
                            <tr className={rowsToCancel[+row.PaymentPlanDetailId] ? 'disabled' : null} key={i}>
                                <td>
                                    <Checkbox
                                        checked={selectedRows[row.PaymentPlanDetailId] != null}
                                        onChange={(selected) => handleCheckboxChange(selected, row)}
                                        className='check'
                                        fullWidthLabel
                                        size={20}
                                    />
                                </td>
                                <td>{BllinkUtils.BllinkTime.parseDate(updatedDateRows[+row.PaymentPlanDetailId] || row.chargeDate)}</td>
                                <td className="amount">
                                    <a rel="noreferrer" href={row.cartID ? `/${constants.pages.orderStatusPage}/${row.cartID}` : ''} target='_blank'>
                                        <CurrencyValue currency={row.currency} >{row.amountInCents}</CurrencyValue>
                                    </a>
                                </td>
                                <td>{row.paidFor}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {selectedAmount ? <div className="panel">
                        <DayPicker
                            className="day-picker"
                            changeDay={changeDay}
                            show={changeDateOpened}
                            limitByDates={chargeDates}
                        />
                        <div className="panel-left">
                            <span className="panel-close" onClick={unselectAll}>&#215;</span>
                        </div>
                        <div className="delimiter"></div>
                        <div className="panel-center">
                            <div className="tab" onClick={cancelPayments}>{t('reports.edit_apartment_payments.cancel_payments')}</div>
                            <div
                                className={changeDateOpened ? 'tab active' : 'tab'}
                                onClick={changeDateOpened ? closeChangeDate : openChangeDate}
                            >
                                {t('reports.edit_apartment_payments.change_charge_date')}
                            </div>
                        </div>
                        <div className="delimiter"></div>
                        <div className="panel-right">
                            <div>
                                <div className="content">
                                    <div className="text">
                                        <span className="num">{selectedAmount}</span>
                                        <br/>
                                        {t('reports.edit_apartment_payments.items_selected')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> : null}
                    <div className="buttons">
                        <button className="bllink-button" onClick={handleApproveClick}>
                            <div className="circle">
                                <img src={LeftArrow} alt=""/>
                            </div>
                            <span className="text">
                                {t('reports.stop_plans.approve')}
                            </span>
                        </button>
                    </div>
                </Fragment>)}
            </Modal.Body>
        </StyledEditApartmentPaymentsModal>
    );
}
